<template>
  <div class="modern-verify-wrapper">
    <div class="verify-container">
      <!-- Status Section -->
      <StatusSection
        :already-submitted="alreadySubmitted"
        :is-expired="isExpired"
        :status="status"
      />

      <!-- Validation Details Section -->
      <ValidationDetailsSection
        :already-submitted="alreadySubmitted"
        :is-expired="isExpired"
        :status="status"
        :validation-date="validationDate"
        :unique-identifier="uniqueIdentifier"
        :validation-link="validationLink"
      />

      <!-- Receipt Details Section -->
      <ReceiptDetailsSection
        :is-expired="isExpired"
        :already-submitted="alreadySubmitted"
        :settled-date="settledDate"
        :invoice-no="invoiceNo"
        :grand-total="grandTotal"
      />

      <!-- Customer Information Section -->
      <CustomerInfoSection
        :already-submitted="alreadySubmitted"
        :is-expired="isExpired"
        :status="status"
        :customer-info="einvoiceCustomer"
      />

      <!-- Softcopy Email Section Submitted -->
      <div 
        v-if="alreadySubmitted && !isExpired && (status === 'Submitted' || status === 'Valid')" 
        class="softcopy-email-section">
        <div class="section-header">
          <h4 class="section-title">Email Notification</h4>
          <p class="section-description">
            Enter your email address to receive status notifications and PDF copy of your e-invoice.
          </p>
        </div>
        
        <div class="email-input-group">
          <label 
            for="softcopy-email" 
            class="email-label">Email Address</label>
          <div class="email-input-wrapper">
            <input
              id="softcopy-email"
              v-model="softcopyEmail"
              type="email"
              class="email-input"
              placeholder="Enter your email address"
            >
            <div class="email-input-icon">
              <svg 
                width="20" 
                height="20" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                viewBox="0 0 24 24">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
          </div>
          <p 
            v-if="emailError" 
            class="email-error">{{ emailError }}</p>
        </div>

        <div
          v-if="alreadySubmitted && !isExpired && status === 'Submitted'" 
          class="email-note">
          <p class="note-text">
            <svg 
              width="16" 
              height="16" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Enter your email address to receive status notifications when you refresh the document status.
          </p>
        </div>
      </div>

      <!-- Action Buttons Section -->
      <ActionButtonsSection
        :already-submitted="alreadySubmitted"
        :is-expired="isExpired"
        :status="status"
        :validation-link="validationLink"
        :consolidated-id="consolidatedId"
        @next="$emit('next')"
        @cancel="$emit('cancel')"
        @show-receipt="showReceipt"
        @refresh-status="showModalRefresh"
        @send-email="sendEmailNotification"
      />
    </div>
    <receipt ref="receipt" />
  </div>
</template>

<script>
import axios from 'axios';
import receipt from '../../../franchise_component/receipt/public-receipt.vue';
import StatusSection from './components/StatusSection.vue';
import ValidationDetailsSection from './components/ValidationDetailsSection.vue';
import ReceiptDetailsSection from './components/ReceiptDetailsSection.vue';
import CustomerInfoSection from './components/CustomerInfoSection.vue';
import ActionButtonsSection from './components/ActionButtonsSection.vue';

export default {
    name: 'VerifyInformation',
    components: {
        receipt,
        StatusSection,
        ValidationDetailsSection,
        ReceiptDetailsSection,
        CustomerInfoSection,
        ActionButtonsSection,
    },
    props: {
        settledDate: {
            type: String,
            required: true,
        },
        invoiceNo: {
            type: String,
            required: true,
        },
        grandTotal: {
            type: String,
            required: true,
        },
        alreadySubmitted: {
            type: Boolean,
            required: true,
        },
        isExpired: {
            type: Boolean,
            required: true,
        },
        validationDate: {
            type: String,
            required: true,
        },
        uniqueIdentifier: {
            type: String,
            required: false,
            default: '',
        },
        status: {
            type: String,
            default: 'Submitted',
        },
        validationLink: {
            type: String,
            required: false,
            default: '',
        },
        transactionId: {
            type: [String, Number],
            required: true,
        },
        franchiseId: {
            type: String,
            required: true,
        },
        einvoiceCustomer: {
            type: Object,
            default: null,
        },
        consolidatedId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            softcopyEmail: '',
            emailError: '',
        };
    },
    computed: {
        isValidEmail() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(this.softcopyEmail);
        },
    },
    mounted() {
        // Initialize softcopy email from einvoice customer data (if available)
        if (this.einvoiceCustomer && this.einvoiceCustomer.email) {
            this.softcopyEmail = this.einvoiceCustomer.email;
        }
    },
    emits: ['next', 'cancel', 'refresh-status'],
    methods: {
        showReceipt() {
            this.$refs.receipt.$emit('show-public-receipt', {
                franchise_id: this.franchiseId,
                transaction_id: this.transactionId,
            });
        },
        showModalRefresh() {
            if (!this.consolidatedId) {
                this.swalError({
                    title: 'Refreshing Error',
                    text: 'Consolidated ID is required for refresh operation.',
                });
                return;
            }

            // Check if softcopy email is available for notification
            if (!this.softcopyEmail || !this.isValidEmail) {
                this.swalError({
                    title: 'Email Required',
                    text:
                        'Please enter and save your email address to receive status notifications.',
                });
                return;
            }

            axios
                .get(
                    Laroute.route('e_invoice_report', {
                        id: this.consolidatedId,
                    }),
                    {
                        params: {
                            franchise_id: this.franchiseId,
                        },
                    },
                )
                .then(response => {
                    let customer = response.data.customer;

                    let ids = [
                        response.data.sequence_number,
                        response.data.receipt_number.substr(0, 6),
                    ];

                    var combinedData = {
                        transaction_id:
                            response.data.transaction[0].transaction_id,
                        name: customer.name,
                        email: customer.email,
                        contact_no: customer.contact_no,
                        address: customer.address,
                        id_no: customer.id_no,
                        enter_id: customer.enter_id,
                        id_type: customer.id_type,
                        consolidated_id: this.consolidatedId,
                        franchise_id: this.franchiseId,
                        ref_id: ids.filter(s => s != null).join('/'),
                        softcopy_email: this.softcopyEmail, // Use saved softcopy_email for notification
                    };
                    this.swalAjaxConfirmation({
                        title: 'Refresh Document',
                        text: 'Are you sure to refresh the document ?',
                        method: 'post',
                        type: 'warning',
                        params: combinedData,
                        confirmButtonColor: '#0CC2AA',
                        url: Laroute.route('e_invoice_refresh'),
                        onSuccess: res => {
                            if (res.value.data.code === 400) {
                                swal({
                                    title: 'Warning',
                                    text: res.value.data.response,
                                    type: 'info',
                                    confirmButtonColor: '#0CC2AA',
                                    confirmButtonText: 'Close',
                                }).then(() => {
                                    swal.close();
                                });
                            } else {
                                swal({
                                    title: 'Success',
                                    text:
                                        'Your data has been reload successfully!',
                                    type: 'success',
                                    confirmButtonColor: '#0CC2AA',
                                    confirmButtonText: 'Close',
                                }).then(() => {
                                    this.$emit('refresh-status');
                                    location.reload();
                                });
                            }
                        },
                        onError: () => {
                            console.log('Error occurred during validation.');
                            this.swalError({
                                title: 'Refreshing Error',
                                text:
                                    'An error occurred during refreshing data. Please try again.',
                            });
                        },
                    });
                })
                .catch(error => {
                    this.swalError({
                        title: 'Refreshing Error',
                        text:
                            'An error occurred during refreshing data. Please try again later.',
                    });
                });
        },
        sendEmailNotification() {
            // Validate email first
            if (!this.softcopyEmail || !this.isValidEmail) {
                this.emailError = 'Please enter a valid email address.';
                this.swalError({
                    title: 'Email Required',
                    text:
                        'Please enter a valid email address to receive the e-invoice notification.',
                });
                return;
            }

            if (!this.consolidatedId) {
                this.swalError({
                    title: 'Error',
                    text: 'Consolidated ID is required for sending email.',
                });
                return;
            }

            // Clear any previous error
            this.emailError = '';

            // Confirm before sending
            this.swalAjaxConfirmation({
                title: 'Send Email Notification',
                text: `Send e-invoice notification to ${this.softcopyEmail}?`,
                method: 'post',
                type: 'info',
                params: {
                    consolidated_id: this.consolidatedId,
                    franchise_id: this.franchiseId,
                    email: this.softcopyEmail,
                },
                confirmButtonColor: '#0CC2AA',
                url: Laroute.route('e_invoice_send_email'),
                onSuccess: res => {
                    swal({
                        title: 'Success',
                        text: 'Email notification has been sent successfully!',
                        type: 'success',
                        confirmButtonColor: '#0CC2AA',
                        confirmButtonText: 'Close',
                    });
                },
                onError: () => {
                    this.swalError({
                        title: 'Email Error',
                        text:
                            'An error occurred while sending the email. Please try again.',
                    });
                },
            });
        },
    },
};
</script>

<style scoped>
.modern-verify-wrapper {
    background: #f8fafc;
    min-height: 100%;
}

.verify-container {
    padding: 24px;
    max-width: 600px;
    margin: 0 auto;
}

/* Softcopy Email Section Styles */
.softcopy-email-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
}

.section-header {
    margin-bottom: 20px;
}

.section-title {
    margin: 0 0 8px 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
}

.section-description {
    margin: 0;
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.5;
}

.email-input-group {
    margin-bottom: 20px;
}

.email-label {
    display: block;
    margin-bottom: 8px;
    font-size: 0.95rem;
    font-weight: 500;
    color: #374151;
}

.email-input-wrapper {
    position: relative;
}

.email-input {
    width: 100%;
    padding: 12px 16px 12px 48px;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s ease;
    background: #fff;
}

.email-input:focus {
    outline: none;
    border-color: #ef5350;
    box-shadow: 0 0 0 3px rgba(239, 83, 80, 0.1);
}

.email-input:disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
}

.email-input-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    pointer-events: none;
}

.email-error {
    margin: 8px 0 0 0;
    font-size: 0.875rem;
    color: #ef4444;
}

.email-note {
    margin-top: 16px;
}

.note-text {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0;
    font-size: 0.875rem;
    color: #64748b;
    line-height: 1.5;
    background: #f8fafc;
    padding: 12px;
    border-radius: 8px;
    border-left: 3px solid #3b82f6;
}

@media (max-width: 768px) {
    .verify-container {
        padding: 16px;
    }

    .softcopy-email-section {
        padding: 20px;
        margin-bottom: 20px;
    }

    .section-title {
        font-size: 1.1rem;
    }

    .email-input {
        padding: 10px 14px 10px 44px;
        font-size: 0.95rem;
    }

    .email-input-icon {
        left: 14px;
    }

    .note-text {
        font-size: 0.8rem;
        padding: 10px;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/e-invoice/portal/verify-info.vue